import {
    Component,
    WritableSignal,
    input,
    output,
    signal,
    computed,
    effect,
    untracked,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { EmailService } from "../../services/email.service";
import { FormsModule } from "@angular/forms";
import { NotificationService } from "../../services/notification.service";
import { S25Util } from "../../util/s25-util";
import { S25RichTextEditorModule } from "../s25-rich-text-editor/s25.rich.text.editor.module";
import { FileUploadModule } from "../s25-file-upload/s25.file.upload.module";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import {
    DataIngestionService,
    FormAssignmentEmailsRequest,
    FormAssignmentRecipient,
    IngestionPoint,
} from "../data-ingestion/data.ingestion.service";
import { ContactService } from "../../services/contact.service";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { ContactRole } from "../../services/organization.service";
import { s25Resource } from "../../util/s25.resource";
import { DBContact } from "../student-housing/student.housing.service";
import { FlsService } from "../../services/fls.service";
import { PreferenceService } from "../../services/preference.service";
import { SeEmailConversationService } from "../student-engagement/email/se.email.conversation.service";
import { Connect25EmailTemplateApi } from "../student-engagement/email/connect25.email.template.api";
import {
    connect25TemplateOrgIdsParamKey,
    loadConnect25TemplatesForOrgIds,
    normalizeConnect25TemplateOrgIds,
    parseConnect25TemplateOrgIdsParamKey,
} from "../student-engagement/email/connect25.email.template.compose.util";
import { fromContactRole, S25EmailRecipientsComponent } from "./s25.email.recipients.component";
import { InstanceCount } from "../../util/s25.instance.count";

type RecipientField = "to" | "cc" | "bcc";

type FieldState = {
    recipients: WritableSignal<FormAssignmentRecipient[]>;
    draftEmail: WritableSignal<string>;
};

@TypeManagerDecorator("s25-ng-email")
@Component({
    selector: "s25-ng-email",
    host: { class: "email-component" },
    template: `
        <!-- Header with CC/BCC toggles -->
        <div class="email-header">
            <h3>Compose Email</h3>
            <div class="header-right">
                @if (!showCc()) {
                    <button class="toggle-link" (click)="showCc.set(true)">+ CC</button>
                }
                @if (!showBcc()) {
                    <button class="toggle-link" (click)="showBcc.set(true)">+ BCC</button>
                }
                @if (showCloseButton()) {
                    <button class="btn-close" (click)="cancel()" title="Close">×</button>
                }
            </div>
        </div>

        @if (canAddCustomFrom()) {
            <div class="form-field">
                <label>
                    <strong>From Address:</strong>
                    <input
                        type="text"
                        aria-label="from address"
                        class="input-field"
                        [ngModel]="fromEmail()"
                        (ngModelChange)="fromEmail.set($event)"
                        [placeholder]="signedInEmail()"
                        [disabled]="disabled()"
                    />
                </label>
            </div>
        }

        <!-- TO -->
        <s25-ng-email-recipients
            [label]="'TO:'"
            [(recipients)]="fields.to.recipients"
            [(draftEmail)]="fields.to.draftEmail"
            [orgMembers]="orgMembers()"
            [memberLabel]="memberLabel()"
            [disabled]="disabled()"
            [resolveNames]="!!attachedForm()"
            (editRequested)="editChip($event, 'to')"
            (nameNeeded)="onNameNeeded($event, 'to')"
            (removed)="onRecipientRemoved($event)"
        />

        <!-- CC -->
        @if (showCc()) {
            <s25-ng-email-recipients
                [label]="'CC:'"
                [(recipients)]="fields.cc.recipients"
                [(draftEmail)]="fields.cc.draftEmail"
                [orgMembers]="orgMembers()"
                [memberLabel]="memberLabel()"
                [disabled]="disabled()"
                (removed)="onRecipientRemoved($event)"
            />
        }

        <!-- BCC -->
        @if (showBcc()) {
            <s25-ng-email-recipients
                [label]="'BCC:'"
                [(recipients)]="fields.bcc.recipients"
                [(draftEmail)]="fields.bcc.draftEmail"
                [orgMembers]="orgMembers()"
                [memberLabel]="memberLabel()"
                [disabled]="disabled()"
                (removed)="onRecipientRemoved($event)"
            />
        }

        <!-- Name prompt for unresolved email address -->
        @if (pendingEmailLookup()) {
            <div class="pending-email-prompt">
                <p>
                    @if (editingChipEmail()) {
                        Enter the name for <strong>{{ pendingEmailLookup()!.email }}</strong
                        >.
                    } @else {
                        <strong>{{ pendingEmailLookup()!.email }}</strong> was not found in contacts. Please enter their
                        name to add them as a new contact.
                    }
                </p>
                <div class="pending-email-fields">
                    <input
                        type="text"
                        placeholder="First name"
                        [ngModel]="pendingFirstName()"
                        (ngModelChange)="pendingFirstName.set($event)"
                    />
                    <input
                        type="text"
                        placeholder="Last name"
                        [ngModel]="pendingFamilyName()"
                        (ngModelChange)="pendingFamilyName.set($event)"
                    />
                </div>
                <div class="pending-email-actions">
                    <s25-ng-button
                        [type]="'primary'"
                        [disabled]="!pendingFirstName().trim() || !pendingFamilyName().trim()"
                        [onClick]="confirmPendingEmailChip.bind(this)"
                        >Add</s25-ng-button
                    >
                    <s25-ng-button [type]="'outline'" [onClick]="cancelPendingEmailChip.bind(this)"
                        >Cancel</s25-ng-button
                    >
                </div>
            </div>
        }

        @if (showConnect25TemplatePicker()) {
            <div class="form-field">
                <label>
                    <strong>Email template:</strong>
                    <select
                        class="input-field"
                        [ngModel]="selectedConnect25TemplateId()"
                        (ngModelChange)="onConnect25TemplateSelected($event)"
                        [disabled]="disabled()"
                    >
                        <option [ngValue]="null">None</option>
                        @for (template of enabledConnect25Templates(); track template.emailTemplateId) {
                            <option [ngValue]="template.emailTemplateId">{{ template.templateName }}</option>
                        }
                    </select>
                </label>
            </div>
        }

        <!-- Subject -->
        <div class="form-field">
            <label>
                <strong>Subject:</strong>
                <input
                    type="text"
                    [ngModel]="subject()"
                    (ngModelChange)="subject.set($event)"
                    class="input-field"
                    placeholder="Enter email subject"
                    [disabled]="disabled()"
                />
            </label>
        </div>

        <!-- Body -->
        <div class="form-field">
            <label>
                <strong>Message:</strong>
                <s25-ng-rich-text-editor
                    [modelValue]="bodyMsg()"
                    (modelValueChange)="bodyMsg.set($event)"
                    [autoResize]="true"
                >
                </s25-ng-rich-text-editor>
            </label>
        </div>

        <!-- Conversation Threading -->
        @if (showConversationToggle()) {
            <div class="form-field">
                <label class="conversation-toggle">
                    <input
                        type="checkbox"
                        [ngModel]="isConvo()"
                        (ngModelChange)="isConvo.set($event)"
                        [disabled]="disabled()"
                    />
                    Enable conversation threading
                </label>
            </div>
        }

        <!-- File Attachments -->
        <div class="form-field">
            <label>
                <strong>Attach Files:</strong>
                <s25-ng-file-upload [showFiles]="true" [onAdd]="handleFileUpload" [multiple]="true">
                </s25-ng-file-upload>
            </label>
        </div>

        <!-- Assign Form -->
        @if (showFormAssignment()) {
            <div class="form-field">
                <div class="form-row-label">
                    <strong [id]="uniqueId">Assign Form:</strong>
                </div>
                @if (!attachedForm()) {
                    <s25-ingestion-point-dropdown
                        [enabledOnly]="true"
                        [ariaLabel]="{ by: uniqueId }"
                        (valueChange)="onFormChosen($event)"
                    />
                } @else {
                    <div class="assigned-form-chip">
                        <span>{{ attachedForm()!.ingestionPointName }}</span>
                        <button
                            class="chip-remove"
                            (click)="attachedForm.set(null)"
                            [disabled]="disabled()"
                            title="Remove assigned form"
                        >
                            ×
                        </button>
                    </div>
                }
            </div>
        }

        <!-- Buttons -->
        <div class="button-group">
            @if (showCloseButton()) {
                <s25-ng-button
                    type="outline"
                    buttonText="Cancel"
                    [disabled]="disabled()"
                    (clicked)="cancel()"
                ></s25-ng-button>
            }
            <s25-ng-button
                type="primary"
                [buttonText]="disabled() ? 'Sending...' : 'Send Email'"
                [disabled]="disabled()"
                (clicked)="send()"
            ></s25-ng-button>
        </div>
    `,
    styles: `
        :host {
            display: block;
            background: var(--color--surface-bg, white);
            padding: 20px;
        }

        .email-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid var(--color--border, #e5e5e5);
            color: var(--color--text, #333);
        }

        .email-header h3 {
            margin: 0;
            font-size: 1.5rem;
            color: var(--color--text, #333) !important;
        }

        .btn-close {
            background: none;
            border: none;
            font-size: 2rem;
            color: var(--color--text-muted, #999);
            cursor: pointer;
            padding: 0;
            width: 30px;
            height: 30px;
            line-height: 1;
        }

        .btn-close:hover {
            color: var(--color--text, #333);
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .toggle-link {
            background: none;
            border: none;
            color: #2573a7;
            font-size: 13px;
            cursor: pointer;
            padding: 2px 6px;
            border-radius: 3px;
        }

        .toggle-link:hover {
            background: rgba(37, 115, 167, 0.08);
        }

        .conversation-toggle {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            cursor: pointer;
        }

        .pending-email-prompt {
            margin-bottom: 1rem;
            padding: 12px 16px;
            background: var(--color--surface-bg-warning, #fff8e1);
            border-left: 3px solid var(--color--warning, #f9a825);
            border-radius: 3px;
        }

        .pending-email-prompt p {
            margin: 0 0 8px;
            color: var(--color--text-warning, #7a5000);
        }

        .pending-email-fields {
            display: flex;
            gap: 8px;
            margin-bottom: 8px;
        }

        .pending-email-fields input {
            flex: 1;
            padding: 6px 8px;
            border: 1px solid var(--color--border, #ccc);
            border-radius: 3px;
            background: var(--color--surface-bg, white);
            color: var(--color--text, #333);
        }

        .pending-email-actions {
            display: flex;
            gap: 8px;
        }

        .form-field {
            margin-bottom: 1rem;
        }

        .form-field > label {
            display: block;
        }

        .form-field > label,
        .form-field > label strong {
            color: var(--color--text, #333) !important;
        }

        .input-field {
            width: 100%;
            padding: 10px;
            margin-top: 5px;
            border: 1px solid var(--color--border, #ddd);
            border-radius: 4px;
            background: var(--color--surface-bg, white);
            color: var(--color--text, #333);
        }

        :host ::ng-deep s25-ng-rich-text-editor .tox-tinymce {
            min-height: 350px !important;
            width: 100%;
        }

        .button-group {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            margin-top: 1.5rem;
        }

        .form-row-label {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
        }

        .assigned-form-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background-color: #f8f9fa;
            border-left: 3px solid #2573a7;
            border-radius: 3px;
            padding: 6px 10px;
            font-size: 13px;
        }

        @media (max-width: 600px) {
            .pending-email-fields {
                flex-direction: column;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        S25EmailRecipientsComponent,
        S25RichTextEditorModule,
        FileUploadModule,
        S25ButtonComponent,
        S25DropdownPaginatedModule,
    ],
})
export class S25EmailComponent {
    private static nextId = InstanceCount.idGenerator({ prefix: "EmailComponent" });

    readonly contextId = input.required<number>();
    readonly itemTypeId = input<number | undefined>(undefined);
    readonly showFormAssignment = input(false);
    readonly orgMembers = input<ContactRole[]>([]);
    readonly initialToRecipients = input<ContactRole[]>([]);
    readonly memberLabel = input("Members");
    readonly showCloseButton = input(true);
    /** Compose, org search, and org turn this on. Other hosts keep the signed-in sender. */
    readonly allowCustomFrom = input(false);
    /** Org ids for Connect25 template picker (one org: full list; multiple: institution intersection). */
    readonly connect25TemplateOrgIds = input<number[]>([]);

    protected readonly uniqueId = S25EmailComponent.nextId();

    readonly connect25TemplateOrgIdsResolved = computed(() =>
        normalizeConnect25TemplateOrgIds(this.connect25TemplateOrgIds() ?? []),
    );

    readonly connect25TemplateOrgIdsParam = computed(() =>
        connect25TemplateOrgIdsParamKey(this.connect25TemplateOrgIds() ?? []),
    );

    readonly connect25TemplatesResource = s25Resource({
        params: () => this.connect25TemplateOrgIdsParam(),
        if: () => this.connect25TemplateOrgIdsParam().length > 0,
        loader: ({ params: orgIdsKey }) =>
            loadConnect25TemplatesForOrgIds(parseConnect25TemplateOrgIdsParamKey(orgIdsKey)),
    });

    readonly enabledConnect25Templates = computed(() => {
        if (this.connect25TemplatesResource.isLoading()) {
            return [];
        }
        return (this.connect25TemplatesResource.value() ?? []).filter((template) => template.enabled !== false);
    });

    readonly showConnect25TemplatePicker = computed(
        () => this.connect25TemplateOrgIdsResolved().length > 0 && this.enabledConnect25Templates().length > 0,
    );

    readonly selectedConnect25TemplateId = signal<number | null>(null);

    private connect25TemplateSelectRequestId = 0;

    readonly flsResource = s25Resource({ loader: () => FlsService.getFls() });
    readonly emailFromPrefsResource = s25Resource({
        if: () => this.allowCustomFrom(),
        loader: () => PreferenceService.getPreferences(["EmailFromAddress"], "S"),
    });
    readonly hasGlobalFrom = computed(() => !!this.emailFromPrefsResource.value()?.EmailFromAddress?.value);
    /** Rights and system-From checks. The placeholder email loads only after this is true. */
    readonly mayAddCustomFrom = computed(() => {
        if (!this.allowCustomFrom() || this.isConvo()) return false;
        if (this.emailFromPrefsResource.isLoading() || this.emailFromPrefsResource.error()) return false;
        if (!this.emailFromPrefsResource.value()) return false;
        if (this.flsResource.isLoading() || this.flsResource.error()) return false;
        const fls = this.flsResource.value();
        return fls?.EMAIL_OPTIONS === "F" && fls?.STUDENT_ENGAGEMENT === "F" && !this.hasGlobalFrom();
    });
    readonly signedInEmailResource = s25Resource({
        if: () => this.mayAddCustomFrom(),
        loader: () => ContactService.getCurrentEmail(),
    });
    readonly signedInEmail = computed(() => this.signedInEmailResource.value() || "");
    readonly canUseConversation = computed(() => this.flsResource.value()?.EMAIL_OPTIONS === "F");
    readonly showConversationToggle = computed(() => this.canUseConversation() && !this.attachedForm());
    readonly isConvo = signal(false);
    readonly lastConvoUuid = signal<string | null>(null);

    readonly closed = output<void>();
    readonly showCc = signal(false);
    readonly showBcc = signal(false);
    readonly fromEmail = signal("");

    readonly fields: Record<RecipientField, FieldState> = {
        to: { recipients: signal([]), draftEmail: signal("") },
        cc: { recipients: signal([]), draftEmail: signal("") },
        bcc: { recipients: signal([]), draftEmail: signal("") },
    };

    readonly pendingEmailLookup = signal<{ field: RecipientField; email: string } | null>(null);
    readonly pendingFirstName = signal("");
    readonly pendingFamilyName = signal("");
    readonly editingChipEmail = signal<{ field: RecipientField; email: string } | null>(null);
    readonly subject = signal("");
    readonly bodyMsg = signal("");
    readonly sending = signal(false);
    readonly disabled = computed(() => this.sending());
    readonly files = signal<{ name: string; data: string; isChosen: boolean }[]>([]);

    readonly attachedForm = signal<IngestionPoint | null>(null);

    readonly canAddCustomFrom = computed(() => this.mayAddCustomFrom() && !this.signedInEmailResource.isLoading());

    private readonly unresolvedEmails = computed(() => {
        if (!this.attachedForm()) return [];
        return this.fields.to
            .recipients()
            .filter((recipient) => !recipient.contId && !recipient.needsName && !recipient.firstName && recipient.email)
            .map((recipient) => recipient.email!);
    });

    private readonly resolvedContactsResource = s25Resource({
        if: () => this.unresolvedEmails().length > 0,
        params: () => this.unresolvedEmails(),
        loader: ({ params: emails }: { params: string[] }) => ContactService.getContactsByEmails(emails),
    });

    private readonly _toInited = signal(false);

    constructor() {
        effect(() => {
            const initial = this.initialToRecipients();
            if (initial.length && !this._toInited()) {
                untracked(() => {
                    this.fields.to.recipients.set(initial.map(fromContactRole));
                    this._toInited.set(true);
                });
            }
        });

        // Auto-resolve plain-email TO chips added before a form was attached.
        effect(() => {
            if (!this.resolvedContactsResource.resource.hasValue()) return;
            untracked(() => this.mergeResolvedContacts(this.resolvedContactsResource.value() ?? []));
        });

        effect(() => {
            if (this.attachedForm()) untracked(() => this.isConvo.set(false));
        });

        effect(() => {
            this.connect25TemplateOrgIdsParam();
            untracked(() => this.selectedConnect25TemplateId.set(null));
        });

        effect(() => {
            const loadError = this.connect25TemplatesResource.error();
            if (loadError) S25Util.showError(loadError);
        });
    }

    async onConnect25TemplateSelected(templateId: number | null): Promise<void> {
        this.selectedConnect25TemplateId.set(templateId);
        if (templateId == null) {
            return;
        }
        const orgIds = this.connect25TemplateOrgIdsResolved();
        if (!orgIds.length) {
            return;
        }
        const selectedId = templateId;
        const requestId = ++this.connect25TemplateSelectRequestId;
        try {
            const fullTemplate = await Connect25EmailTemplateApi.getForOrg(selectedId, orgIds[0], true);
            if (
                requestId !== this.connect25TemplateSelectRequestId ||
                this.selectedConnect25TemplateId() !== selectedId
            ) {
                return;
            }
            this.subject.set(fullTemplate.subject ?? "");
            this.bodyMsg.set(fullTemplate.bodyHtml ?? "");
        } catch (caughtError: unknown) {
            if (
                requestId !== this.connect25TemplateSelectRequestId ||
                this.selectedConnect25TemplateId() !== selectedId
            ) {
                return;
            }
            S25Util.showError(caughtError);
        }
    }

    // ── Helpers ─────────────────────────────────────────────────────────────────

    private mergeResolvedContacts(contacts: DBContact[]) {
        const contactByEmail = new Map(contacts.map((contact) => [contact.email?.toLowerCase(), contact]));
        this.fields.to.recipients.update((recipients) =>
            recipients.map((recipient) => {
                if (recipient.contId || recipient.needsName || !recipient.email) return recipient;
                const contact = contactByEmail.get(recipient.email.toLowerCase());
                if (!contact) return { ...recipient, needsName: true };
                const firstName = contact.firstName ?? "";
                const familyName = contact.familyName ?? "";
                return {
                    contId: contact.contId,
                    email: recipient.email,
                    firstName,
                    familyName,
                    displayName: (contact.fullName ?? `${firstName} ${familyName}`).trim(),
                };
            }),
        );
    }

    // ── Recipient editing ────────────────────────────────────────────────────────

    /** A typed address the recipient row could not match to a contact needs a name from the user. */
    onNameNeeded(email: string, field: RecipientField) {
        this.pendingEmailLookup.set({ field, email });
        this.pendingFirstName.set("");
        this.pendingFamilyName.set("");
    }

    onRecipientRemoved(email: string) {
        // Close the name prompt if it targets the chip being removed
        if (this.pendingEmailLookup()?.email === email) this.cancelPendingEmailChip();
    }

    editChip(chip: FormAssignmentRecipient, field: RecipientField) {
        this.editingChipEmail.set({ field, email: chip.email! });
        this.pendingFirstName.set(chip.firstName ?? "");
        this.pendingFamilyName.set(chip.familyName ?? "");
        this.pendingEmailLookup.set({ field, email: chip.email! });
    }

    confirmPendingEmailChip() {
        const pending = this.pendingEmailLookup();
        if (!pending) return;
        const firstName = this.pendingFirstName().trim();
        const familyName = this.pendingFamilyName().trim();
        if (!firstName || !familyName) return;

        const f = this.fields[pending.field];
        const exists = f.recipients().some((recipient) => recipient.email === pending.email);
        const displayName = `${firstName} ${familyName}`;
        if (exists) {
            f.recipients.update((recipients) =>
                recipients.map((recipient) =>
                    recipient.email === pending.email
                        ? { ...recipient, firstName, familyName, displayName, needsName: false }
                        : recipient,
                ),
            );
        } else {
            f.recipients.update((recipients) => [
                ...recipients,
                { email: pending.email, firstName, familyName, displayName },
            ]);
        }
        this.pendingEmailLookup.set(null);
        this.pendingFirstName.set("");
        this.pendingFamilyName.set("");
        this.editingChipEmail.set(null);
    }

    cancelPendingEmailChip() {
        this.pendingEmailLookup.set(null);
        this.pendingFirstName.set("");
        this.pendingFamilyName.set("");
        this.editingChipEmail.set(null);
    }

    // ── Form assignment ──────────────────────────────────────────────────────────

    onFormChosen(item: DropDownItem) {
        if (!item) return;
        const form = (item as any).data as IngestionPoint;
        if (form?.ingestionPointId) {
            this.attachedForm.set(form);
        }
    }

    handleFileUpload = async (file: S25File): Promise<void> => {
        const newFile = { name: file.name ?? "", data: (file.data as string) ?? "", isChosen: true };
        this.files.update((current) => [...current, newFile]);
    };

    // ── Send ─────────────────────────────────────────────────────────────────────

    send() {
        this.sending.set(true);
        // ANG-2674: 250ms timeout allows ngModel to flush before we read values
        setTimeout(async () => {
            try {
                const getRecipientRefs = (field: RecipientField) => {
                    const fieldState = this.fields[field];
                    const inputs = [...fieldState.recipients(), fieldState.draftEmail().trim() || ""].filter(Boolean);
                    return EmailService.joinRecipientRefs(inputs);
                };

                const tos = getRecipientRefs("to");
                if (!tos) {
                    NotificationService.post("Please choose at least one recipient");
                    this.sending.set(false);
                    return;
                }

                if (!this.subject().trim()) {
                    NotificationService.post("Please enter a subject");
                    this.sending.set(false);
                    return;
                }

                if (!this.bodyMsg()?.trim()) {
                    NotificationService.post("Please enter a message");
                    this.sending.set(false);
                    return;
                }

                const fromAddress = this.customFromAddress();
                if (fromAddress && !S25Util.isValidEmail(fromAddress)) {
                    NotificationService.post("Please enter a valid from address");
                    this.sending.set(false);
                    return;
                }

                const form = this.attachedForm();
                const body = await this.buildBody();

                if (form?.ingestionPointId) {
                    // Guard: all TO recipients must have names before form emails are sent
                    if (this.fields.to.recipients().some((recipient) => recipient.needsName)) {
                        NotificationService.post("Please resolve all recipient names in the TO field before sending");
                        this.sending.set(false);
                        return;
                    }

                    const strip = ({ needsName: _, displayName: __, ...r }: FormAssignmentRecipient) => r;
                    const uploadedFiles = this.files()
                        .filter((file) => file.isChosen)
                        .map((file) => ({ name: file.name, data: file.data }));
                    const attachments = EmailService.getAttachments({ reports: [], uploadedFiles });
                    const request: FormAssignmentEmailsRequest = {
                        subject: this.subject(),
                        body,
                        recipients: {
                            to: this.fields.to.recipients().map(strip),
                            cc: this.fields.cc.recipients().map(strip),
                            bcc: this.fields.bcc.recipients().map(strip),
                        },
                        attachments: attachments.length ? attachments : undefined,
                        from: fromAddress,
                    };

                    const emailPromise = DataIngestionService.sendFormAssignmentEmails(form.ingestionPointId, request)
                        .then((resp) => {
                            if (resp?.error) {
                                NotificationService.post("Email(s) failed: " + resp.error);
                                this.sending.set(false);
                            } else {
                                NotificationService.post("Email(s) have been successfully sent");
                                this.sending.set(false);
                                this.cancel();
                            }
                        })
                        .catch((resp: unknown) => {
                            const errorData = (resp as any)?.error?.results?.error ?? (resp as any)?.error ?? resp;
                            NotificationService.post(
                                "Email(s) failed: " + S25Util.errorText(errorData, "Uncaught Error"),
                            );
                            this.sending.set(false);
                        });
                    NotificationService.post("Email(s) are sending...", emailPromise);
                } else {
                    const uploadedFiles = this.files()
                        .filter((f) => f.isChosen)
                        .map((f) => ({ name: f.name, data: f.data }));

                    const sendingAsConvo = this.isConvo();
                    const emailPromise = EmailService.sendEmail(
                        this.subject(),
                        body,
                        tos,
                        fromAddress,
                        getRecipientRefs("cc"),
                        getRecipientRefs("bcc"),
                        undefined,
                        undefined,
                        uploadedFiles,
                        this.contextId(),
                        this.itemTypeId(),
                        undefined,
                        undefined,
                        undefined,
                        sendingAsConvo || undefined,
                    )
                        .then((resp) => {
                            if (resp?.error) {
                                NotificationService.post("Email(s) failed: " + resp.error);
                                this.sending.set(false);
                            } else {
                                if (sendingAsConvo) {
                                    const uuid = SeEmailConversationService.extractConvoUuid(resp);
                                    this.lastConvoUuid.set(uuid);
                                }
                                NotificationService.post("Email(s) have been successfully sent");
                                this.sending.set(false);
                                this.cancel();
                            }
                        })
                        .catch((resp: unknown) => {
                            const errorData = (resp as any)?.error?.results?.error ?? (resp as any)?.error ?? resp;
                            NotificationService.post(
                                "Email(s) failed: " + S25Util.errorText(errorData, "Uncaught Error"),
                            );
                            this.sending.set(false);
                        });
                    NotificationService.post("Email(s) are sending...", emailPromise);
                }
            } catch (e) {
                const message = e instanceof Error ? e.message : "Uncaught Error";
                NotificationService.post("Email(s) failed: " + message);
                this.sending.set(false);
            }
        }, 250);
    }

    /** Blank From keeps the signed-in user. Conversations do not use it. */
    private customFromAddress(): string | undefined {
        if (!this.canAddCustomFrom()) return undefined;
        const fromAddress = this.fromEmail().trim();
        return fromAddress || undefined;
    }

    private async buildBody(): Promise<string> {
        const tinymceContent: string | undefined = (window as any).tinymce?.activeEditor?.getContent();
        const base = tinymceContent ?? this.bodyMsg() ?? "";
        const form = this.attachedForm();
        if (!form?.ingestionPointId) return base;

        const origin = S25Util.getWindowOrigin();
        const path = S25Util.getWindowPath();
        const formName = form.ingestionPointName ?? "Form";
        const link = `${origin}${path}#!/form-submission/{{submissionUuid}}`;

        return (
            base +
            `<p style="padding:8px 12px; border-left:3px solid #2573a7; border-radius:3px; background-color:#f8f9fa;">` +
            `<strong>Assigned form:</strong> ${formName}<br>` +
            `<a href="${link}" style="color:#2573a7;">Click here to fill out the form</a>` +
            `</p>`
        );
    }

    cancel() {
        this.closed.emit();
    }
}
